import React, {
  ReactElement,
  useContext,
  createContext,
  ForwardedRef,
  forwardRef,
} from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import request from 'utils/api/request';
import { useUser } from './user';
import { UserPaymentMethod } from 'types/PaymentMethods';
import { useAccount } from './account';
import { PaymentMethodsWidgetCallbacks } from 'components/PaymentMethodsWidget/PaymentMethodsWidget';

interface UseEnabledElementsResult {
  /** Filtered elements based on options */
  enabledPaymentMethods: UserPaymentMethod[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  isError: boolean;
  /** Error object if any */
  error: unknown;
  /** Refetch function */
  refetch: () => void;
  isPayBridgeEnabled: boolean;
}

/**
 * Hook that fetches elements from API, filters them, and memoizes the result
 * @param options - Configuration options for the hook
 * @returns Object containing elements and query state
 */
export const useEnabledPaymentMethods = (): UseEnabledElementsResult => {
  const { provider } = useUser();
  const { account } = useAccount();

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  }: UseQueryResult<UserPaymentMethod[], unknown> = useQuery(
    [account?.id],
    async () => {
      if (!account || !provider) {
        return [];
      }

      if (!provider.enablePSPIntegrationsV2) {
        return [];
      }

      const response = await request<{
        data: UserPaymentMethod[];
        status: number;
      }>({
        path: `/1/providers/${provider!.id}/payment-methods`,
        method: 'GET',
      });

      // Country Specific Payment Methods
      const countryMethods = response.data.filter(
        (e) =>
          e.countryCode?.toUpperCase() ===
            account?.billing?.countryCode?.toUpperCase() && e.enabled
      );
      if (countryMethods.length > 0) {
        return countryMethods;
      }

      const fallbackMethods = response.data.filter(
        (e) => e.countryCode === undefined && e.enabled
      );
      return fallbackMethods;
    },
    {
      staleTime: 1 * 60 * 1000,
      cacheTime: 2 * 60 * 1000,
    }
  );

  const methods = data ?? [];

  return {
    enabledPaymentMethods: methods,
    isPayBridgeEnabled:
      provider?.enablePSPIntegrationsV2 === true && methods.length > 0,
    isLoading,
    isError,
    error,
    refetch,
  };
};

const PaymentMethodsWidgetContext = createContext<
  { ref: ForwardedRef<PaymentMethodsWidgetCallbacks> } | undefined
>(undefined);

type PaymentMethodsWidgetProviderProps = {
  children: ReactElement;
};
export const PaymentMethodsWidgetProvider = forwardRef<
  PaymentMethodsWidgetCallbacks,
  PaymentMethodsWidgetProviderProps
>(({ children }, ref) => {
  return (
    <PaymentMethodsWidgetContext.Provider value={{ ref }}>
      {children}
    </PaymentMethodsWidgetContext.Provider>
  );
});

export const usePaymentMethodsWidgetConsumer = () => {
  return useContext(PaymentMethodsWidgetContext);
};
